import { Component, ViewChild, ElementRef, AfterViewInit, OnInit, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { EnergyDataService } from '../../../energy-data/energy-data.service';
import { LwOverallEnergyGraph } from '../../lw-graphs/energy-overall-graph';
import { LdsEventBusService } from '../../../services/lds-event-bus.service';
import { LdsUserService } from '../../../services/lds-user.service';
import { FancyDonutGraph } from '@lightwaverf/simple-d3-graph';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { PopupComponent } from '../../../common-components/popup/popup.component';
import { DataExportService } from '../../../services/data-export.service';
import { GlobalVariablesService } from "../../../services/global-variables.service";
import { FeatureSetsData } from 'src/app/lib/interfaces/data-analytics';
import { EnergyTotalByCategory } from 'src/app/lib/interfaces/data-analytics';
import { DataAnalyticsService } from 'src/app/lib/data-analytics/data-analytics.service';
import { time } from "../../../services/global-variables.service"
import { ChartJsPieGraph } from '../../lw-graphs/chart-js-pie';
import { ChartJsEnergyOverallGraph } from '../../lw-graphs/chart-js-energy-overall';
import { Input } from '@angular/core';
import { Chart } from 'chart.js';
import { OverviewService } from '../../overview.service';

@Component({
    selector: 'lds-y3-energy-overview-chart',
    templateUrl: './y3-energy-overview-chart.component.html',
    styleUrls: ['./y3-energy-overview-chart.component.css'],
    standalone: false
})
export class Y3EnergyOverviewChartComponent implements AfterViewInit, OnInit, OnChanges, OnDestroy {
  @ViewChild('line', { static: false }) lineArea: ElementRef;
  @ViewChild('pie', { static: false }) pieArea: ElementRef;
  barChart: any;
  pieChart: any;
  selected;
  noData: boolean;
  private timespan: string;
  private startTime;
  private endTime;
  public loading1: boolean = true;
  public loading2: boolean = true;
  public firstDraw = true;

  constructor(
    private overviewData: OverviewService, private energyData: EnergyDataService, private globalVars: GlobalVariablesService, 
    private eventBus: LdsEventBusService, private router: Router,
    public user: LdsUserService, private dialog: MatDialog, private dataExport: DataExportService, private dataAnalytics: DataAnalyticsService) { }

  ngOnInit() {
    this.user.loading = true;
  }

  ngOnDestroy() {
    if (this.pieChart) {
      this.pieChart.destroy();
    }

    if (this.barChart) {
      this.barChart.destroy();
    }

  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.firstChange) {
      this.redrawGraphs();
    }
  }

  sortDataValue(data) {
    data.sort((a,b) => {
      const diff = a.value - b.value
      return diff
    })
  }

  ngAfterViewInit() {
    this.timespanChange();

    this.eventBus.subscribe('energySummary-featureSets-loaded', (data) => {
      this.selected = this.energyData.getSelectedFeatureSets()
      
      if (this.selected.length) {
        this.user.loading = false;
        this.redrawGraphs();
        
        this.eventBus.startSubscribe('energySummary-featureSets-loaded', (event) => {
          this.selected = this.energyData.getSelectedFeatureSets()

          if(this.selected !== this.energyData.getSelectedFeatureSets()){
            this.overviewData.clearData();
          }
          this.redrawGraphs();
        });
      } else {
        this.user.loading = false;
        // user logs in if no data but dashboard won't show anything
      }
    });
  }

  changeStartTime(startTime) {
    const date = new Date(startTime + time.day);
    date.setHours(1, 1, 0, 0);
    return date.getTime();
  }

  private async redrawGraphs() {
    const chartExists = Chart.getChart("energyOverallGraph");
    if (chartExists) {
      chartExists.destroy();
    }

    const miniGraphExists = Chart.getChart("pieGraph");
    if (miniGraphExists) {
      miniGraphExists.destroy();
    }

    let EBD;
    let EBC;
    this.loading1 = true;
    this.loading2 = true;


    const dateNow = new Date();
    dateNow.setHours(23, 59, 59, 999);

    const overviewCachedData = this.overviewData.getDataCache(this.timespan);

    if (overviewCachedData) {
      EBD = overviewCachedData.EBD;
      EBC = overviewCachedData.EBC;

      this.drawBarChart(EBD);
      this.drawPieGraph(EBC);

    } else {

      switch (this.timespan) {
        case "weekly":
          this.endTime = dateNow.getTime();
          this.startTime = this.changeStartTime(this.endTime - time.week);
          // Use pre-loaded daily_data instead of making new HTTP requests
          this.usePreloadedData('weekly');
          break;
        case "monthly":
          this.endTime = dateNow.getTime();
          this.startTime = this.changeStartTime(this.endTime - time.day * 30);
          // Use pre-loaded monthly_data instead of making new HTTP requests
          this.usePreloadedData('monthly');
          break;
        case "custom":
          // Custom date range still needs HTTP requests
          this.calculateEnergyByDate();
          return;
      }
    }

    this.eventBus.broadcast('overview-status-update', EBD);
    this.noData = EBC && EBC.length && EBD && EBD.length ? false : true;
    
    if (!this.noData) {
      this.pieChart.destroy();
      this.pieChart = undefined;
      this.loading1 = true;
      this.drawPieGraph(EBC);
      this.barChart.destroy();
      this.barChart = undefined;
      this.loading2 = true;
      this.drawBarChart(EBD);

    }
  }




  private async drawBarChart(EBD?) {
    let data;
    if (EBD) {
      data = EBD;
    } else {
      data = await this.energyData.calculateTotalEnergyByDate(this.selected);
    }
    this.eventBus.broadcast('overview-status-update', data);
    this.noData = data.length ? false : true;
    if (data.length) {
      this.loading2 = false;
      this.firstDraw = false;
      if (this.barChart) {
        this.barChart.destroy();
      }
      const barChart = new ChartJsEnergyOverallGraph(this.globalVars);
      if (this.user.settings) {
        barChart.draw(data);
        this.barChart = barChart.graph;
      } else {
        this.eventBus.startSubscribe("user-settings-loaded", () => {
          barChart.draw(data);
          const dataPoint0 = data[0];
          const time = new Date(dataPoint0.timestamp);
          if (time.toDateString() !== new Date(this.startTime).toDateString()) {
            data.remove(dataPoint0);
          }
          this.barChart = barChart.graph;
        });
      }
    }
  }

  private async drawPieGraph(EBC?) {
    let data;
    if (EBC) {
      data = EBC;
    } else {
      data = await this.energyData.calculateEnergyByCategory(this.selected);
    }
    this.noData = data.length ? false : true;
    
    // Sort by value descending (highest to lowest)
    data.sort((a, b) => b.value - a.value);
    
    // Show top 10 devices, combine the rest as "Other"
    const maxItems = 10;
    if (data.length > maxItems) {
      const topItems = data.slice(0, maxItems);
      const otherItems = data.slice(maxItems);
      const otherTotal = otherItems.reduce((sum, item) => sum + item.value, 0);
      
      if (otherTotal > 0) {
        topItems.push({
          label: 'Other',
          value: otherTotal,
          deviceId: null,
          deviceType: null,
          groupId: null,
          room: null
        });
      }
      data = topItems;
    }

    this.loading1 = false;

    const pieChart = new ChartJsPieGraph(this.router);

    pieChart.draw(data);

    this.pieChart = pieChart.graph;

  }

  /**
   * Use pre-loaded data from loadEnergySummaryData instead of making new HTTP requests.
   * This significantly improves performance by reusing the data that was already fetched.
   */
  private async usePreloadedData(timespan: 'weekly' | 'monthly') {
    // For weekly, use daily_data; for monthly, use monthly_data
    // But always filter to the correct date range to handle cached data
    const dataKey = timespan === 'weekly' ? 'daily_data' : 'monthly_data';
    const daysToShow = timespan === 'weekly' ? 7 : 30;
    
    // Calculate the cutoff timestamp (start of the date range)
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const cutoffTime = now.getTime() - (daysToShow * time.day);
    
    // Aggregate data by timestamp (same approach as calculateEnergyByDate)
    let aggregated = {};
    
    for (const featureSet of this.selected) {
      // Use monthly_data as the source since it has all the data
      // daily_data may have been incorrectly populated in cached data
      const data = featureSet.monthly_data || featureSet[dataKey];
      if (data && data.length) {
        // Filter to only include data within the desired date range
        const filteredData = data.filter(item => item.timestamp >= cutoffTime);
        // Use the existing aggregation method which groups by date
        aggregated = this.energyData.aggregateEnergyDailyData(filteredData, aggregated);
      }
    }
    
    // Convert aggregated object to sorted array
    let localArray = [];
    for (const timestamp in aggregated) {
      if (aggregated.hasOwnProperty(timestamp)) {
        localArray.push({
          timestamp: +timestamp,
          value: Number((aggregated[timestamp] / 1000).toFixed(1))
        });
      }
    }
    
    // Sort by timestamp ascending
    localArray.sort((a, b) => a.timestamp - b.timestamp);
    
    // Ensure we only have the correct number of days (take most recent)
    if (localArray.length > daysToShow) {
      localArray = localArray.slice(-daysToShow);
    }

    // Calculate energy by category using pre-loaded totals
    const EBC = timespan === 'weekly' 
      ? await this.energyData.calculateEnergyByCategory(this.selected)
      : await this.energyData.calculateMonthlyEnergyByCategory(this.selected);

    this.overviewData.setDataCache(timespan, {
      EBD: localArray,
      EBC: EBC
    });

    this.loading1 = false;
    this.loading2 = false;
    this.drawBarChart(localArray);
    this.drawPieGraph(EBC);
    this.eventBus.broadcast('overview-status-update', localArray);
  }


  private async calculateEnergyByDate() {
    let local = [];
    let featureSetStore = {};
    let featureSets = {};
    let localArray = [];

    for (let index = 0; index < this.selected.length; index++) {
      let featureSet = this.selected[index];
      for (let featureId in featureSet.features) {
        let feature = this.user.features[featureSet.features[featureId]];
        if (feature.attributes.type !== "energy") {
          continue;
        }

        let res = await this.dataAnalytics.getCustomDateRange(feature.featureId, this.dataAnalytics.getDaysBetween(this.startTime, this.endTime), this.startTime, this.endTime, "energy");
        local = this.energyData.aggregateEnergyDailyData(res, local);
        if (index === this.selected.length - 1) {
          for (const key in local) {
            if (local.hasOwnProperty(key)) {
              localArray.unshift({
                timestamp: +key,
                value: Number((local[key] / 1000).toFixed(1))
              });
            }
          }
        }

        featureSetStore[featureSet.featureId] = res;
        featureSets[featureSet.featureId] = featureSet;
      }
    }
    
    this.overviewData.setDataCache(this.timespan, {
      EBD: localArray,
    });
    
    this.calculateCustomEnergyByCategory(featureSetStore);
    this.loading1 = false;
    this.drawBarChart(localArray);
    this.eventBus.broadcast('overview-status-update', localArray);
  }

  onExportClick() {
    let data: DataFormat[] = [];
    let runningTotalCost = 0;
    let runningTotalEnergy = 0;
    for (let i = 0; i < this.energyData.featureSetsWithData.length; i++) {
      const featureSet = this.energyData.featureSetsWithData[i];
      const cost = Number.parseFloat(featureSet.monthly_total) * this.globalVars.getTarrif();
      runningTotalCost += cost;
      runningTotalEnergy += Number.parseInt(featureSet.monthly_total);
      data[i] = {
        name: featureSet.name,
        monthly_energy_usage: featureSet.monthly_total,
        cost: cost,
        total_cost: '',
        total_energy_usage: ''
      };
    }
    data[data.length - 1].total_cost = runningTotalCost;
    data[data.length - 1].total_energy_usage = runningTotalEnergy;
    const dialogRef = this.dialog.open(PopupComponent);
    dialogRef.afterClosed().subscribe(result => {
      if (!result) {
        this.dataExport.exportToCsv('lightwave-data', data, ['Name', 'Energy Usage', 'Cost', 'Total Cost', 'Total Energy Usage']);
        return;
      }
      for (let i = 0; i < data.length; i++) {
        let cost = this.roundCost(data[i].cost);
        data[i].cost = '£' + cost;
        if (data[i].total_cost !== '') {
          let totalCost = '£' + this.roundCost(data[i].total_cost);
          data[i].total_cost = totalCost;
        }
      }
      this.dataExport.exportToCsv('lightwave-data', data, ['Name', 'Energy Usage', 'Cost', 'Total Cost', 'Total Energy Usage']);
    });


  }

  roundCost(cost) {
    cost = cost * 100;
    cost = Math.round(cost);
    cost = cost / 100;
    return cost;
  }

  timespanChange() {
    this.eventBus.subscribe("overview-timespan-change", (res) => {
      let timespan;
      if (!res || !res.timespan) {
        timespan = 'weekly';
      } else {
        timespan = res.timespan;
      }
      this.timespan = timespan;

      if (this.timespan === "custom") {
        this.startTime = res.startDate.getTime();
        this.endTime = res.endDate.getTime();
      }

      if (this.pieChart) {
        this.redrawGraphs();
      }
    })
  }

  async calculateCustomEnergyByCategory(featureSetsData: any) {
    let local = [];
    for (let i in this.selected) {
      let featureSetData = featureSetsData[this.selected[i].featureId];
      let featureSet = this.selected[i];

      let groupId = featureSet.groupId;
      let room = this.energyData.findRoomName(groupId);
      let deviceId = featureSet.deviceId;
      let name = featureSet.name;
      let deviceType = featureSet.type;
      let total = 0;
      for (let i = 0; i < featureSetData.length; i++) {
        let dataPoint = featureSetData[i];
        total += dataPoint.value;
      }

      if (Math.abs(+total)) {
        local.push({
          label: name,
          value: Number(total) / 1000,
          deviceId: deviceId,
          deviceType: deviceType,
          groupId: groupId,
          room: room
        });
      }
    }

    this.sortDataValue(local)

    this.loading2 = false;
    // this.pieChart.show();
    if (this.pieChart) {
      this.pieChart.destroy();
    }
    const cached = this.overviewData.getDataCache(this.timespan);
    cached["EBC"] = local;
    this.overviewData.setDataCache(this.timespan, cached);
    this.drawPieGraph(local);
  }

}

export class DataFormat {
  name: string;
  monthly_energy_usage: any;
  cost: any;
  total_cost: any;
  total_energy_usage: any;
}


